import React, {useEffect, useState} from 'react';
import {Button, Form, Input, InputNumber, message, Modal, Select, Space, Switch, Tag} from 'antd';
import {ReloadOutlined} from '@ant-design/icons';
import {getMenuRegistry, validateMenu} from '../api/menuApi';

/*
 props:
  open: boolean
  mode: 'create' | 'edit'
  parentMenu: object | null (create-child 시 부모)
  record: object | null (edit 대상)
  onSubmit(values)
  onCancel()
*/
const MenuCreateEditModal = ({ open, mode='create', parentMenu=null, record=null, onSubmit, onCancel, initialValues=null }) => {
  const [form] = Form.useForm();
  const [registry, setRegistry] = useState([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const isEdit = mode === 'edit';

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        setRegistryLoading(true);
        const res = await getMenuRegistry();
        let list = Array.isArray(res?.data) ? res.data : [];
        // 편집 또는 초기값으로 전달된 라우트키를 레지스트리에 포함
        if (isEdit && record?.rteKey && !list.includes(record.rteKey)) {
          list = [record.rteKey, ...list];
        }
        if (!isEdit && initialValues?.rteKey && !list.includes(initialValues.rteKey)) {
          list = [initialValues.rteKey, ...list];
        }
        setRegistry(list);
      } catch (e) {
        setRegistry([]);
      } finally { setRegistryLoading(false); }
    };
    load();
  }, [open, isEdit, record?.rteKey, initialValues?.rteKey]);

  const reloadRegistry = async () => {
    try {
      setRegistryLoading(true);
      const res = await getMenuRegistry();
      const list = Array.isArray(res?.data) ? res.data : [];
      setRegistry(list);
      message.success('라우트키를 새로고침했습니다.');
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
    } finally { setRegistryLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    if (isEdit && record) {
      form.setFieldsValue({
        menuNm: record.menuNm,
        // url 제거
        path: record.path || '',
        rteKey: record.rteKey || undefined,
        sortSeq: record.sortSeq ?? 0,
        useYn: record.useYn !== 'N',
        icon: record.icon || '',
        description: record.description || ''
      });
    } else {
      form.resetFields();
      const base = { sortSeq: 0, useYn: true };
      const init = initialValues ? { ...initialValues } : {};
      if (init.path) {
        init.path = normalizePath(init.path);
      }
      if (init.useYn !== undefined && init.useYn !== null) {
        init.useYn = init.useYn === true || init.useYn === 'Y';
      }
      if (init.sortSeq === undefined || init.sortSeq === null) {
        init.sortSeq = base.sortSeq;
      }
      form.setFieldsValue({ ...base, ...init });
    }
  }, [open, isEdit, record, form, initialValues]);

  const runDuplicateCheck = async (opts = { notify: true }) => {
    try {
      setValidating(true);
      const values = form.getFieldsValue();
      if (!values.path && !values.rteKey) {
        if (opts.notify) message.info('검증할 항목이 없습니다.');
        return true; // nothing to validate
      }
      const res = await validateMenu({ path: values.path || null, rteKey: values.rteKey || null, excludeMenuId: record?.menuId });
      const dup = res?.data;
      if (dup?.pathDuplicate) {
        form.setFields([ { name: 'path', errors: ['이미 사용중인 경로입니다.'] } ]);
        return false;
      }
      if (dup?.rteKeyDuplicate) {
        form.setFields([ { name: 'rteKey', errors: ['이미 사용중인 라우트키입니다.'] } ]);
        return false;
      }
      if (opts.notify) message.success('중복 없음');
      return true;
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
      return false;
    } finally { setValidating(false); }
  };

  const normalizePath = (val) => {
    if (!val) return val;
    let s = String(val).trim();
    if (!s.startsWith('/')) s = '/' + s;
    // 연속 슬래시 정규화
    s = s.replace(/\/+/, '/');
    return s;
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const pass = await runDuplicateCheck({ notify: false });
      if (!pass) return;
      const payload = {
        menuNm: values.menuNm?.trim(),
        parentMenuId: isEdit ? record?.parentMenuId : (parentMenu ? parentMenu.menuId : null),
        path: values.path ? normalizePath(values.path) : null,
        rteKey: values.rteKey?.trim() || null,
        sortSeq: values.sortSeq ?? 0,
        useYn: values.useYn ? 'Y' : 'N',
        icon: values.icon?.trim() || null,
        description: values.description?.trim() || null
      };
      onSubmit?.(payload);
    } catch (e) { /* validation errors handled */ }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? '메뉴 수정' : (parentMenu ? '하위 메뉴 생성' : '루트 메뉴 생성')}
      onCancel={onCancel}
      maskClosable={false}
      width={640}
      footer={[
        <Button key="cancel" onClick={onCancel}>취소</Button>,
        <Button key="dup" loading={validating} onClick={() => runDuplicateCheck({ notify: true })}>중복검증</Button>,
        <Button key="ok" type="primary" loading={validating} onClick={handleOk}>{isEdit ? '저장' : '생성'}</Button>
      ]}
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        {!isEdit && parentMenu && (
          <Form.Item label="부모 메뉴">
            <Tag color="blue">{parentMenu.menuNm} ({parentMenu.menuId})</Tag>
          </Form.Item>
        )}
        <Form.Item name="menuNm" label="메뉴명" rules={[{ required: true, message: '메뉴명을 입력하세요' }]} extra="화면과 좌측 트리에 표시되는 이름입니다.">
          <Input placeholder="메뉴명을 입력" maxLength={100} onBlur={(e)=>form.setFieldsValue({ menuNm: e.target.value.trim() })} />
        </Form.Item>
        <Form.Item
          name="path"
          label="경로"
          tooltip="ex) /projects"
          rules={[{
            validator: (_, v) => {
              if (!v) return Promise.resolve();
              if (String(v).includes(' ')) return Promise.reject('공백을 포함할 수 없습니다.');
              if (!String(v).startsWith('/')) return Promise.reject('경로는 "/"로 시작해야 합니다.');
              return Promise.resolve();
            }
          }]}
          extra="경로를 사용하세요."
        >
          <Input placeholder="/로 시작하는 경로" allowClear maxLength={255} onBlur={(e)=>form.setFieldsValue({ path: normalizePath(e.target.value) })} />
        </Form.Item>
        <Form.Item name="rteKey" label="라우트키" tooltip="프론트 컴포넌트 매핑 키"
          dependencies={["path"]}
          rules={[({ getFieldValue }) => ({
            validator: (_, v) => {
              const pathVal = getFieldValue('path');
              if (pathVal && (!v || String(v).trim() === '')) {
                return Promise.reject('경로를 설정한 경우 라우트키가 필요합니다.');
              }
              return Promise.resolve();
            }
          })]}
        >
          <Space.Compact style={{ width: '100%' }}>
            <Select
              allowClear
              placeholder="라우트키 선택"
              options={registry.map(r => ({ value: r, label: r }))}
              loading={registryLoading}
              showSearch
              notFoundContent={registryLoading ? '로딩 중...' : '등록된 라우트키가 없습니다.'}
              filterOption={(inp,o)=>o?.value?.toLowerCase?.().includes(inp.toLowerCase())}
              style={{ flex: 1 }}
              onChange={(val) => form.setFieldsValue({ rteKey: val })}
            />
            <Button icon={<ReloadOutlined />} onClick={reloadRegistry} disabled={registryLoading} />
          </Space.Compact>
        </Form.Item>
        <Form.Item name="sortSeq" label="정렬순서" extra="같은 부모 내에서 작은 숫자부터 정렬됩니다.">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item name="useYn" label="사용여부" valuePropName="checked">
          <Switch checkedChildren="Y" unCheckedChildren="N" />
        </Form.Item>
        <Form.Item name="icon" label="아이콘" extra="아이콘 키 또는 커스텀 클래스명을 입력합니다.">
          <Input placeholder="아이콘 키" maxLength={100} onBlur={(e)=>form.setFieldsValue({ icon: e.target.value.trim() })} />
        </Form.Item>
        <Form.Item name="description" label="설명">
          <Input.TextArea rows={3} maxLength={500} showCount placeholder="메뉴 설명" onBlur={(e)=>form.setFieldsValue({ description: e.target.value.trim() })} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MenuCreateEditModal;
